import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../lib/auth';
import { ApiError, apiFetch } from '../lib/api';
import { setStoredToken } from '../lib/tokenStore';

/**
 * Sign-in form for `/sign-in`.
 *
 * Posts directly to the Worker `/admin/v1/auth/sign-in` (CORS allowed).
 * On success the JWT is stashed in localStorage via `setStoredToken` and
 * `apiFetch` attaches it as `Authorization: Bearer <token>` on every
 * subsequent admin request. Sign-out clears it.
 *
 * Validates Requirements 20.1, 20.2, 20.3, 25.1.
 */

const SignInSchema = z.object({
  email: z
    .string()
    .min(1, 'Vui lòng nhập email')
    .email('Email không hợp lệ'),
  password: z.string().min(1, 'Vui lòng nhập mật khẩu'),
});

type SignInValues = z.infer<typeof SignInSchema>;

interface SignInResponse {
  userId: string;
  expiresAt: number;
  token: string;
}

export default function SignInPage(): JSX.Element {
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<SignInValues>({
    resolver: zodResolver(SignInSchema),
    defaultValues: { email: '', password: '' },
  });

  // Single submission-error string. Per Requirement 20.3 we deliberately do
  // NOT distinguish between "wrong email" and "wrong password".
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitRequestId, setSubmitRequestId] = useState<string | null>(null);

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    setSubmitRequestId(null);
    try {
      const res = await apiFetch<SignInResponse>('/admin/v1/auth/sign-in', {
        method: 'POST',
        body: values,
        silent: true,
      });
      setStoredToken(res.token);
      setAuth(res.userId, res.expiresAt);
      navigate('/packs', { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setSubmitError('Email hoặc mật khẩu không đúng');
        } else {
          setSubmitError('Có lỗi xảy ra. Thử lại.');
          setSubmitRequestId(err.requestId);
        }
        return;
      }
      // Network or unexpected runtime error: surface the network-down copy
      // shared with the global banner (Requirement 25.2 sibling).
      setSubmitError('Mất kết nối tới máy chủ. Thử lại.');
    }
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
        <h1 className="text-2xl font-semibold text-slate-900">Đăng nhập</h1>
        <p className="mt-1 text-sm text-slate-500">
          Bảng điều khiển nội dung Skey
        </p>

        <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-slate-700"
            >
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              autoFocus
              disabled={isSubmitting}
              aria-invalid={errors.email ? 'true' : 'false'}
              {...register('email')}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
            />
            {errors.email ? (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.email.message}
              </p>
            ) : null}
          </div>

          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-slate-700"
            >
              Mật khẩu
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              disabled={isSubmitting}
              aria-invalid={errors.password ? 'true' : 'false'}
              {...register('password')}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm shadow-sm focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500 disabled:bg-slate-100 disabled:text-slate-500"
            />
            {errors.password ? (
              <p className="mt-1 text-xs text-red-600" role="alert">
                {errors.password.message}
              </p>
            ) : null}
          </div>

          {submitError ? (
            <div
              role="alert"
              className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              <p>{submitError}</p>
              {submitRequestId ? (
                <p className="mt-1 text-xs text-red-600">
                  Mã yêu cầu:{' '}
                  <code className="font-mono">{submitRequestId}</code>
                </p>
              ) : null}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSubmitting ? 'Đang đăng nhập…' : 'Đăng nhập'}
          </button>
        </form>
      </div>
    </div>
  );
}
